import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { enqueueSnackbar } from '~/utils/notifications';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

type InterpolationScope = 'allEmpty' | 'current';

const toggleGroupSx = {
    '& .MuiToggleButton-root': {
        py: 0.5,
        px: 0.75,
        flex: 1,
        fontSize: 11,
        lineHeight: 1,
        textTransform: 'none',
        whiteSpace: 'nowrap',
    },
};

export default function InterpolationOperation() {
    const mainView = useRecoilValue(mainViewState);
    const environment = useRecoilValue(environmentState);
    const selectedROI = useRecoilValue(selectedROIState);
    const roi = environment?.structureSet?.getRoi(selectedROI);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const selectedROIIndex = useRecoilValue(selectedROIState);

    const [scope, setScope] = useState<InterpolationScope>('allEmpty');

    const isStructuresLoaded: undefined | boolean =
        currentStructureSet && selectedROIIndex >= 0;

    const handleScopeChange = (
        _: React.MouseEvent<HTMLElement>,
        value: InterpolationScope | null
    ) => {
        if (value !== null) {
            setScope(value);
        }
    };

    const handleApply = () => {
        if (roi?.canModify === false) {
            enqueueSnackbar(
                <FormattedMessage id="structureCannotBeModified" />,
                { variant: 'error' }
            );
            return;
        }

        if (scope === 'allEmpty') {
            if (currentStructureSet && roi) {
                environment?.handleInterpolateAllSlices(roi);
            }
        } else if (scope === 'current') {
            if (currentStructureSet && roi && mainView) {
                const planeCoordinates = mainView.position[2];
                environment?.handleInterpolateSingleSlice(
                    roi,
                    planeCoordinates
                );
            }
        }
    };

    if (!isStructuresLoaded) {
        return (
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="pleaseSelectStructure" />
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="body2" fontSize={12}>
                    <FormattedMessage id="interpolation" />
                </Typography>
                <ToggleButtonGroup
                    value={scope}
                    exclusive
                    onChange={handleScopeChange}
                    size="small"
                    sx={toggleGroupSx}
                >
                    <ToggleButton value="allEmpty">
                        <FormattedMessage id="allEmpty" />
                    </ToggleButton>
                    <ToggleButton value="current">
                        <FormattedMessage id="current" />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Button
                variant="outlined"
                color="primary"
                size="small"
                fullWidth
                onClick={handleApply}
                sx={{ fontSize: 11, textTransform: 'none' }}
            >
                <FormattedMessage id="interpolate" />
            </Button>
        </Box>
    );
}
